import Button from "components/Button/Button";
import ButtonIcon from "components/ButtonIcon/ButtonIcon";
import Card from "components/Card/Card";
import { KaboodleToaster } from "components/common";
import Select from "components/inputs/Select/Select";
import KaboodleModal from "components/Modals/KaboodleModal";
import { Tooltip } from "components/Tooltip/Tooltip";
import { useEffect, useMemo, useState } from "react";
import {
  DOCUMENT_GROUP_TAG_LABELS,
  DOCUMENT_GROUP_TAG_TYPES,
  useGetDocumentGroupTagsQuery,
  type DocumentGroupTagType,
} from "services/document-group-tag-api";
import {
  useExtractTaggedDocumentMutation,
  useGenerateAllocatorNotesMutation,
  useGenerateCombinedPdfMutation,
  type ExtractableDocumentType,
} from "services/metadoc-api";
import { extractErrorMessage, isFetchBaseQueryError } from "utils/error-utils";

import * as Sentry from "@sentry/react";

import { useDocumentGroupTagExtractionPolling } from "../hooks/page-operations/useDocumentGroupTagExtractionPolling";
import { useEnabledDocumentGroupTagTypes } from "../hooks/page-operations/useEnabledDocumentGroupTagTypes";

type GenerateCombinedPdfButtonProps = {
  metadocId: string;
  msaId?: string;
  costProjectionId?: string;
  extractOnly?: boolean;
};

type GenerateCombinedPdfOption = {
  label: string;
  value: string;
  extract?: {
    documentType: ExtractableDocumentType;
    documentName: string;
    tagType: DocumentGroupTagType;
  };
};

const BASE_GENERATE_COMBINED_PDF_OPTIONS: GenerateCombinedPdfOption[] = [
  {
    label: "Generate combined PDF",
    value: "GENERATE_COMBINED_PDF",
  },
  {
    label: "Regenerate AI Notes",
    value: "REGENERATE_AI_NOTES",
  },
];

const EXTRACT_RATED_AGE_REQUEST_OPTION: GenerateCombinedPdfOption = {
  label: "Records for Rated Age",
  value: "EXTRACT_RATED_AGE_REQUEST",
  extract: {
    documentType: "RECORDS_FOR_RATED_AGE",
    documentName: "Records for Rated Age",
    tagType: DOCUMENT_GROUP_TAG_TYPES.RECORDS_FOR_RATED_AGE,
  },
};

const EXTRACT_FIRST_REPORT_OF_INJURY_OPTION: GenerateCombinedPdfOption = {
  label: "First Report of Injury",
  value: "EXTRACT_FIRST_REPORT_OF_INJURY",
  extract: {
    documentType: "FIRST_REPORT_OF_INJURY",
    documentName: "First Report of Injury",
    tagType: DOCUMENT_GROUP_TAG_TYPES.FROI,
  },
};

const EXTRACT_MEDICAL_RECORDS_FOR_SUBMISSION_OPTION: GenerateCombinedPdfOption =
  {
    label: "Medical Records for Submission",
    value: "EXTRACT_MEDICAL_RECORDS_FOR_SUBMISSION",
    extract: {
      documentType: "MEDICAL_RECORDS_FOR_CMS_SUBMISSION",
      documentName: "Medical Records for CMS Submission",
      tagType: DOCUMENT_GROUP_TAG_TYPES.CMS_SUBMISSION,
    },
  };

const EXTRACT_OPTIONS = [
  EXTRACT_RATED_AGE_REQUEST_OPTION,
  EXTRACT_FIRST_REPORT_OF_INJURY_OPTION,
  EXTRACT_MEDICAL_RECORDS_FOR_SUBMISSION_OPTION,
];

type GenerateCombinedPdfModalProps = {
  isOpen: boolean;
  onClose: () => void;
  metadocId: string;
  msaId?: string;
  costProjectionId?: string;
  extractOnly?: boolean;
};

/* metadoc modal for triggering a single combined-pdf generation action.
   mirrors existing document-generation UX with a constrained one-option flow. */
const GenerateCombinedPdfModal = ({
  isOpen,
  onClose,
  metadocId,
  msaId,
  costProjectionId,
  extractOnly,
}: GenerateCombinedPdfModalProps) => {
  const [selectedOption, setSelectedOption] =
    useState<GenerateCombinedPdfOption | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [generateCombinedPdf] = useGenerateCombinedPdfMutation();
  const [generateAllocatorNotes] = useGenerateAllocatorNotesMutation();
  const [extractTaggedDocument] = useExtractTaggedDocumentMutation();
  const { trackJob: trackDocumentGroupTagExtractionJob } =
    useDocumentGroupTagExtractionPolling();

  const enabledTagTypes = useEnabledDocumentGroupTagTypes();
  const generateCombinedPdfOptions = useMemo(() => {
    const extractOptions = EXTRACT_OPTIONS.filter(
      (option) =>
        option.extract && enabledTagTypes.includes(option.extract.tagType),
    );
    return extractOnly
      ? extractOptions
      : [...BASE_GENERATE_COMBINED_PDF_OPTIONS, ...extractOptions];
  }, [enabledTagTypes, extractOnly]);

  const selectedExtract = selectedOption?.extract;
  const isExtractOption = Boolean(selectedExtract);
  const metadocIdNumber = Number(metadocId);

  // Just an existence check to gate the button, not a list to choose from,
  // extraction always covers every active tag. Fetches the full list for
  // that, there's no lighter endpoint, not worth adding one at this scale.
  const { data: documentGroupTags, isFetching: isFetchingTagOptions } =
    useGetDocumentGroupTagsQuery(metadocIdNumber, {
      skip: !isOpen || !isExtractOption,
    });
  const hasNoTags =
    isExtractOption &&
    !isFetchingTagOptions &&
    !(documentGroupTags ?? []).some(
      (tag) => tag.type === selectedExtract?.tagType,
    );

  useEffect(() => {
    if (!selectedOption) {
      setSelectedOption(generateCombinedPdfOptions[0]);
    }
  }, [selectedOption, generateCombinedPdfOptions]);

  const handleGenerate = async () => {
    if (!selectedOption || !metadocId) {
      return;
    }

    setIsSubmitting(true);

    try {
      if (selectedOption.value === "REGENERATE_AI_NOTES") {
        if (Number.isNaN(metadocIdNumber)) {
          KaboodleToaster.error("Invalid metadoc ID.");
          return;
        }
        await generateAllocatorNotes({ metadocId: metadocIdNumber }).unwrap();
        KaboodleToaster.success("AI notes regeneration started.");
      } else if (selectedExtract) {
        if (hasNoTags) {
          return;
        }
        const response = await extractTaggedDocument({
          metadocId,
          documentType: selectedExtract.documentType,
          msaId,
          costProjectionId,
        }).unwrap();
        trackDocumentGroupTagExtractionJob(response.background_job.id);
        KaboodleToaster.success(
          `${selectedExtract.documentName} extraction started.`,
        );
      } else {
        await generateCombinedPdf({ metadocId }).unwrap();
        KaboodleToaster.success("Combined PDF generation started.");
      }
      onClose();
    } catch (error) {
      const isRejectedRequest =
        isFetchBaseQueryError(error) &&
        typeof error.status === "number" &&
        error.status >= 400 &&
        error.status < 500;
      if (!isRejectedRequest) {
        Sentry.captureException(error, {
          tags: { feature: "metadoc", action: "generate_document" },
          extra: { metadocId, selectedOption: selectedOption.value },
        });
      }
      KaboodleToaster.error(
        extractErrorMessage(
          error,
          "Failed to start request. Please try again.",
        ),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const actionLabel = isExtractOption
    ? "Extract"
    : (selectedOption?.label ??
      (extractOnly ? "Extract" : "Generate Combined PDF"));

  return (
    <KaboodleModal
      isOpen={isOpen}
      onClose={onClose}
      footer={
        <div className="flex jc-c">
          <Button
            onClick={handleGenerate}
            disabled={
              !selectedOption ||
              isSubmitting ||
              (isExtractOption && (isFetchingTagOptions || hasNoTags))
            }
          >
            {isSubmitting ? "Submitting..." : actionLabel}
          </Button>
          <Button onClick={onClose}>Cancel</Button>
        </div>
      }
    >
      <Card heading="Document Generation">
        {/* Extract Type labels every option on purpose, per the ticket. */}
        <Select
          label="Extract Type"
          name="combinedPdfGeneration"
          placeholder="Select extract type..."
          options={generateCombinedPdfOptions}
          value={selectedOption}
          onChange={(option) =>
            setSelectedOption(option as GenerateCombinedPdfOption)
          }
          required
          isClearable={false}
        />
        {isExtractOption && isFetchingTagOptions && <p>Loading tags...</p>}
        {selectedExtract && !isFetchingTagOptions && hasNoTags && (
          <p>
            No {DOCUMENT_GROUP_TAG_LABELS[selectedExtract.tagType]} tags found
            on this metadoc. Assign one before extracting.
          </p>
        )}
      </Card>
    </KaboodleModal>
  );
};

/* sidebar trigger for metadoc combined-pdf generation. */
const GenerateCombinedPdfButton = ({
  metadocId,
  msaId,
  costProjectionId,
  extractOnly,
}: GenerateCombinedPdfButtonProps) => {
  const [showModal, setShowModal] = useState(false);

  return (
    <>
      <Tooltip content="Generate Document">
        <ButtonIcon kind="neutral" onClick={() => setShowModal(true)}>
          downloading
        </ButtonIcon>
      </Tooltip>

      <GenerateCombinedPdfModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        metadocId={metadocId}
        msaId={msaId}
        costProjectionId={costProjectionId}
        extractOnly={extractOnly}
      />
    </>
  );
};

export default GenerateCombinedPdfButton;
